// banking/src/pages/onboarding/corporate/steps/Step7_ReviewAndSubmit.tsx

import React, { useState } from 'react';
import { getTranslation, type Language } from '../corporate-translations';
import type { CorporateFormData } from '../CorporateOnboarding';
import { getCurrentApiUrl } from '../../../../config/server.config';

interface Step7Props {
  formData: CorporateFormData;
  updateFormData: (data: Partial<CorporateFormData>) => void;
  language: Language;
  errors: Record<string, string>;
  onSubmit: () => void;
  termsAccepted: boolean;
  setTermsAccepted: (accepted: boolean) => void;
}

const Step7_ReviewAndSubmit: React.FC<Step7Props> = ({
  formData,
  language,
  errors,
  onSubmit,
  termsAccepted,
  setTermsAccepted,
}) => {

  const handleOpenTerms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getCurrentApiUrl()}/ai/terms-pdf-url`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.open(data.url, '_blank');
      } else {
        console.error('Error fetching terms PDF URL');
        alert('Error al abrir los términos y condiciones. Por favor, intente nuevamente.');
      }
    } catch (error) {
      console.error('Error opening terms:', error);
      alert('Error al abrir los términos y condiciones. Por favor, intente nuevamente.');
    }
  };

  const handleSubmit = () => {
    if (!termsAccepted) {
      alert(
        language === 'es'
          ? 'Debe aceptar los términos y condiciones para continuar'
          : language === 'fr'
          ? 'Vous devez accepter les termes et conditions pour continuer'
          : language === 'de'
          ? 'Sie müssen die Geschäftsbedingungen akzeptieren, um fortzufahren'
          : language === 'it'
          ? 'Devi accettare i termini e condizioni per continuare'
          : language === 'pt'
          ? 'Você deve aceitar os termos e condições para continuar'
          : language === 'nl'
          ? 'U moet de algemene voorwaarden accepteren om door te gaan'
          : language === 'ru'
          ? 'Вы должны принять условия для продолжения'
          : language === 'ar'
          ? 'يجب عليك قبول الشروط والأحكام للمتابعة'
          : 'You must accept the terms and conditions to continue'
      );
      return;
    }
    onSubmit();
  };

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          {getTranslation('step7_title', language)}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {getTranslation('step7_subtitle', language)}
        </p>
      </div>

      {/* Company Information Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {language === 'es'
            ? '1. Información de la Empresa'
            : language === 'fr'
            ? '1. Informations sur l\'entreprise'
            : language === 'de'
            ? '1. Unternehmensinformationen'
            : language === 'it'
            ? '1. Informazioni sull\'azienda'
            : language === 'pt'
            ? '1. Informações da Empresa'
            : language === 'nl'
            ? '1. Bedrijfsinformatie'
            : language === 'ru'
            ? '1. Информация о компании'
            : language === 'ar'
            ? '1. معلومات الشركة'
            : '1. Company Information'
          }
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">{getTranslation('company_name_label', language)}</p>
            <p className="font-medium">{formData.companyName || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">{getTranslation('legal_name_label', language)}</p>
            <p className="font-medium">{formData.legalName || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">{getTranslation('company_type_label', language)}</p>
            <p className="font-medium">{formData.companyType || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">{getTranslation('tax_id_label', language)}</p>
            <p className="font-medium">{formData.taxId || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">{getTranslation('registration_number_label', language)}</p>
            <p className="font-medium">{formData.registrationNumber || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">{getTranslation('registration_country_label', language)}</p>
            <p className="font-medium">{formData.registrationCountry || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">{getTranslation('business_sector_label', language)}</p>
            <p className="font-medium">{formData.businessSectorCode || '-'}</p>
          </div>
        </div>
      </div>

      {/* Contact and Address Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {language === 'es'
            ? '2. Contacto y Dirección'
            : language === 'fr'
            ? '2. Contact et Adresse'
            : language === 'de'
            ? '2. Kontakt und Adresse'
            : language === 'it'
            ? '2. Contatto e Indirizzo'
            : language === 'pt'
            ? '2. Contato e Endereço'
            : language === 'nl'
            ? '2. Contact en Adres'
            : language === 'ru'
            ? '2. Контакт и Адрес'
            : language === 'ar'
            ? '2. الاتصال والعنوان'
            : '2. Contact and Address'
          }
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-500">{getTranslation('company_email_label', language)}</p>
            <p className="font-medium">{formData.companyEmail || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">{getTranslation('company_phone_label', language)}</p>
            <p className="font-medium">
              {formData.companyPhoneCountryCode} {formData.companyPhoneNumber}
              {formData.companyPhoneExtension && ` ext. ${formData.companyPhoneExtension}`}
            </p>
          </div>
          <div className="md:col-span-2">
            <p className="text-gray-500">{getTranslation('address_line1_label', language)}</p>
            <p className="font-medium">{formData.addressLine1 || '-'}</p>
          </div>
          {formData.website && (
            <div className="md:col-span-2">
              <p className="text-gray-500">{getTranslation('website_label', language)}</p>
              <p className="font-medium">
                <a
                  href={formData.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#B3A369] hover:underline"
                >
                  {formData.website}
                </a>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Company Documents Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {language === 'es'
            ? '3. Documentos de la Empresa'
            : language === 'fr'
            ? '3. Documents de l\'entreprise'
            : language === 'de'
            ? '3. Unternehmensdokumente'
            : language === 'it'
            ? '3. Documenti dell\'azienda'
            : language === 'pt'
            ? '3. Documentos da Empresa'
            : language === 'nl'
            ? '3. Bedrijfsdocumenten'
            : language === 'ru'
            ? '3. Документы компании'
            : language === 'ar'
            ? '3. وثائق الشركة'
            : '3. Company Documents'
          }
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center">
            {formData.businessRegistration ? (
              <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-gray-300 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <span>{getTranslation('business_registration_label', language)}</span>
          </div>
          <div className="flex items-center">
            {formData.articlesOfIncorporation ? (
              <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-gray-300 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <span>{getTranslation('articles_of_incorporation_label', language)}</span>
          </div>
          <div className="flex items-center">
            {formData.businessUtilityBill ? (
              <svg className="h-5 w-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="h-5 w-5 text-gray-300 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <span>{getTranslation('business_utility_bill_label', language)}</span>
          </div>
        </div>
      </div>

      {/* Representatives Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {language === 'es'
            ? '4. Representantes Legales'
            : language === 'fr'
            ? '4. Représentants légaux'
            : language === 'de'
            ? '4. Gesetzliche Vertreter'
            : language === 'it'
            ? '4. Rappresentanti legali'
            : language === 'pt'
            ? '4. Representantes Legais'
            : language === 'nl'
            ? '4. Wettelijke vertegenwoordigers'
            : language === 'ru'
            ? '4. Законные представители'
            : language === 'ar'
            ? '4. الممثلون القانونيون'
            : '4. Legal Representatives'
          }
        </h3>
        <div className="space-y-4">
          {formData.representatives.map((rep, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">
                    {rep.firstName} {rep.middleName} {rep.lastName}
                  </h4>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">{getTranslation('role_label', language)}: </span>
                      <span className="font-medium">{rep.role || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{getTranslation('ownership_percentage_label', language)}: </span>
                      <span className="font-medium">{rep.ownershipPercentage}%</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{getTranslation('document_type_label', language)}: </span>
                      <span className="font-medium">{rep.documentType || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{getTranslation('email_label', language)}: </span>
                      <span className="font-medium">{rep.email || '-'}</span>
                    </div>
                  </div>
                </div>
                <div className="ml-4">
                  {Object.values(rep.documents || {}).filter(Boolean).length > 0 ? (
                    <div className="flex items-center text-green-600">
                      <svg className="h-5 w-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm">
                        {Object.values(rep.documents || {}).filter(Boolean).length}{' '}
                        {language === 'es' ? 'docs' : 'docs'}
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center text-gray-400">
                      <svg className="h-5 w-5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      <span className="text-sm">
                        {language === 'es' ? 'Sin docs' : 'No docs'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div className="mt-4 p-4 bg-[#F5F3E8] border border-[#E5DDB8] rounded-lg">
            <p className="text-sm text-[#9A8B57]">
              <span className="font-medium">
                {language === 'es'
                  ? 'Total de representantes:'
                  : language === 'fr'
                  ? 'Total de représentants :'
                  : language === 'de'
                  ? 'Gesamtzahl der Vertreter:'
                  : language === 'it'
                  ? 'Totale rappresentanti:'
                  : language === 'pt'
                  ? 'Total de representantes:'
                  : language === 'nl'
                  ? 'Totaal vertegenwoordigers:'
                  : language === 'ru'
                  ? 'Всего представителей:'
                  : language === 'ar'
                  ? 'إجمالي الممثلين:'
                  : 'Total representatives:'
                }{' '}
              </span>
              {formData.representatives.length}
            </p>
            <p className="text-sm text-[#9A8B57] mt-1">
              <span className="font-medium">
                {language === 'es'
                  ? 'Propiedad total:'
                  : language === 'fr'
                  ? 'Propriété totale :'
                  : language === 'de'
                  ? 'Gesamteigentum:'
                  : language === 'it'
                  ? 'Proprietà totale:'
                  : language === 'pt'
                  ? 'Propriedade total:'
                  : language === 'nl'
                  ? 'Totaal eigendom:'
                  : language === 'ru'
                  ? 'Общая собственность:'
                  : language === 'ar'
                  ? 'الملكية الإجمالية:'
                  : 'Total ownership:'
                }{' '}
              </span>
              {formData.representatives.reduce((sum, rep) => sum + rep.ownershipPercentage, 0)}%
            </p>
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {language === 'es'
            ? '5. Términos y Condiciones'
            : language === 'fr'
            ? '5. Termes et Conditions'
            : language === 'de'
            ? '5. Geschäftsbedingungen'
            : language === 'it'
            ? '5. Termini e Condizioni'
            : language === 'pt'
            ? '5. Termos e Condições'
            : language === 'nl'
            ? '5. Algemene Voorwaarden'
            : language === 'ru'
            ? '5. Условия'
            : language === 'ar'
            ? '5. الشروط والأحكام'
            : '5. Terms and Conditions'
          }
        </h3>

        {/* Open Terms Button */}
        <div className="mb-4">
          <button
            type="button"
            onClick={handleOpenTerms}
            className="inline-flex items-center px-4 py-2 border border-blue-300 rounded-lg shadow-sm text-sm font-medium text-white bg-[#B3A369] hover:bg-[#9A8B57] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#B3A369]"
          >
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {language === 'es'
              ? 'Abrir Términos y Condiciones'
              : language === 'fr'
              ? 'Ouvrir les Termes et Conditions'
              : language === 'de'
              ? 'Geschäftsbedingungen öffnen'
              : language === 'it'
              ? 'Apri Termini e Condizioni'
              : language === 'pt'
              ? 'Abrir Termos e Condições'
              : language === 'nl'
              ? 'Algemene Voorwaarden openen'
              : language === 'ru'
              ? 'Открыть Условия'
              : language === 'ar'
              ? 'فتح الشروط والأحكام'
              : 'Open Terms and Conditions'
            }
          </button>
        </div>

        {/* Checkbox */}
        <div className="flex items-start">
          <div className="flex items-center h-5">
            <input
              id="terms"
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              className="w-4 h-4 text-[#B3A369] border-gray-300 rounded focus:ring-[#B3A369]"
            />
          </div>
          <label htmlFor="terms" className="ml-3 text-sm text-gray-700">
            {language === 'es'
              ? 'He leído y acepto los Términos y Condiciones de BELOBABA. Entiendo que la información proporcionada será verificada y que cualquier información falsa puede resultar en el rechazo de mi solicitud.'
              : language === 'fr'
              ? 'J\'ai lu et j\'accepte les Termes et Conditions de BELOBABA. Je comprends que les informations fournies seront vérifiées et que toute information fausse peut entraîner le rejet de ma demande.'
              : language === 'de'
              ? 'Ich habe die Geschäftsbedingungen von BELOBABA gelesen und akzeptiere sie. Ich verstehe, dass die bereitgestellten Informationen überprüft werden und dass falsche Informationen zur Ablehnung meines Antrags führen können.'
              : language === 'it'
              ? 'Ho letto e accetto i Termini e Condizioni di BELOBABA. Comprendo che le informazioni fornite saranno verificate e che qualsiasi informazione falsa può comportare il rifiuto della mia richiesta.'
              : language === 'pt'
              ? 'Li e aceito os Termos e Condições da BELOBABA. Entendo que as informações fornecidas serão verificadas e que qualquer informação falsa pode resultar na rejeição do meu pedido.'
              : language === 'nl'
              ? 'Ik heb de Algemene Voorwaarden van BELOBABA gelezen en accepteer deze. Ik begrijp dat de verstrekte informatie zal worden geverifieerd en dat onjuiste informatie kan leiden tot afwijzing van mijn aanvraag.'
              : language === 'ru'
              ? 'Я прочитал и принимаю Условия BELOBABA. Я понимаю, что предоставленная информация будет проверена, и любая ложная информация может привести к отклонению моей заявки.'
              : language === 'ar'
              ? 'لقد قرأت وأوافق على شروط وأحكام BELOBABA. أفهم أنه سيتم التحقق من المعلومات المقدمة وأن أي معلومات خاطئة قد تؤدي إلى رفض طلبي.'
              : 'I have read and accept the Terms and Conditions of BELOBABA. I understand that the information provided will be verified and that any false information may result in the rejection of my application.'
            }
          </label>
        </div>
        {errors.terms && (
          <p className="mt-2 text-sm text-red-600">{errors.terms}</p>
        )}
      </div>
    </div>
  );
};

export default Step7_ReviewAndSubmit;
